import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors — including stale lazy-chunk load failures after a new
 * deploy (which would otherwise leave #root empty). On a chunk error we reload
 * once (guarded by sessionStorage) to fetch the fresh chunk; otherwise we show
 * a minimal recovery UI instead of a blank page.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isChunkError =
      /loading (chunk|css chunk|dynamically imported module)/i.test(error?.message || "") ||
      error?.name === "ChunkLoadError";

    if (isChunkError && !sessionStorage.getItem("chunk-reload")) {
      sessionStorage.setItem("chunk-reload", "1");
      window.location.reload();
      return;
    }
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-background text-foreground">
          <h1 className="font-heading text-2xl font-bold">Something went wrong</h1>
          <p className="text-muted-foreground">Please refresh the page to continue.</p>
          <button
            onClick={() => {
              sessionStorage.removeItem("chunk-reload");
              window.location.reload();
            }}
            className="rounded-xl bg-primary px-5 py-2.5 font-semibold text-primary-foreground"
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
